import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Download, Share2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, apiErr } from "@/lib/api";

export const S = 1080;

export function wrap(ctx, text, maxW) {
  const words = text.split(" "), out = [];
  let line = "";
  words.forEach((w) => { const t = line ? `${line} ${w}` : w; if (ctx.measureText(t).width > maxW && line) { out.push(line); line = w; } else { line = t; } });
  if (line) { out.push(line); }
  return out;
}

export function fitText(ctx, text, weight, start, maxW) {
  let size = start;
  do { ctx.font = `${weight} ${size}px Outfit, sans-serif`; size -= 4; } while (ctx.measureText(text).width > maxW && size > 40);
}

export function drawBackdrop(ctx) {
  ctx.fillStyle = "#0B132B"; ctx.fillRect(0, 0, S, S);
  [["#00C2CB", 120, 110, 180], ["#FF70A6", 980, 160, 120], ["#FFB703", 960, 960, 200], ["#00C2CB", 90, 990, 110]].forEach(([col, x, y, r]) => {
    ctx.globalAlpha = 0.9; ctx.fillStyle = col; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
  });
  ctx.globalAlpha = 1;
}

export const monthYear = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" }) : "");

function draw(c, d) {
  const ctx = c.getContext("2d");
  drawBackdrop(ctx);
  ctx.fillStyle = "#FFFFFF"; ctx.beginPath(); ctx.roundRect(90, 150, S - 180, S - 300, 48); ctx.fill();
  ctx.strokeStyle = "#FFB703"; ctx.lineWidth = 10; ctx.beginPath(); ctx.roundRect(116, 176, S - 232, S - 352, 36); ctx.stroke();
  ctx.textAlign = "center"; ctx.fillStyle = "#00A3AB";
  ctx.font = "800 30px 'Plus Jakarta Sans', sans-serif"; ctx.fillText("ADVENTURE ROCK BERMUDA", S / 2, 270);
  ctx.fillStyle = "#0B132B"; ctx.font = "800 74px Outfit, sans-serif"; ctx.fillText("Thank You", S / 2, 370);
  ctx.fillStyle = "#475569"; ctx.font = "500 30px 'Plus Jakarta Sans', sans-serif"; ctx.fillText("is proudly presented to", S / 2, 430);
  ctx.fillStyle = "#FF4D8D"; fitText(ctx, d.business_name, 800, 84, S - 320);
  ctx.fillText(d.business_name, S / 2, 540);
  ctx.fillStyle = "#FFB703"; ctx.beginPath(); ctx.roundRect(S / 2 - 260, 590, 520, 78, 39); ctx.fill();
  ctx.fillStyle = "#0B132B"; ctx.font = "800 34px Outfit, sans-serif"; ctx.fillText(d.tier_name.toUpperCase(), S / 2, 642);
  ctx.fillStyle = "#334155"; ctx.font = "500 30px 'Plus Jakarta Sans', sans-serif";
  const msg = `for supporting Bermuda's families and helping Explorers play, learn and grow${d.area ? ` in ${d.area}` : ""}.`;
  wrap(ctx, msg, S - 360).forEach((l, i) => ctx.fillText(l, S / 2, 730 + i * 42));
  ctx.fillStyle = "#94A3B8"; ctx.font = "600 24px 'Plus Jakarta Sans', sans-serif"; ctx.fillText(`Official Partner · ${monthYear(d.paid_on)}`, S / 2, 860);
  ctx.fillStyle = "#FFFFFF"; ctx.font = "800 34px Outfit, sans-serif"; ctx.fillText("#SupportTheRock", S / 2, 1010);
}

export function ShareImagePage({ endpoint, title, intro, paint, fileName, shareText, footer }) {
  const ref = useRef(null);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(endpoint).then((r) => setD(r.data)).catch((e) => setErr(apiErr(e))); }, [endpoint]);
  useEffect(() => {
    if (d && ref.current) { document.fonts.ready.then(() => paint(ref.current, d)); }
  }, [d, paint]);
  const blob = () => new Promise((res) => ref.current.toBlob(res, "image/png"));
  const name = d ? `${fileName(d).toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png` : "adventure-rock.png";
  const download = async () => {
    const a = document.createElement("a"); a.href = URL.createObjectURL(await blob()); a.download = name; a.click();
  };
  const share = async () => {
    const file = new File([await blob()], name, { type: "image/png" });
    const text = shareText(d);
    if (navigator.canShare?.({ files: [file] })) {
      try { await navigator.share({ files: [file], text }); } catch (e) { /* user cancelled */ }
    } else { await navigator.clipboard.writeText(`${text} ${window.location.href}`); toast.success("Caption and link copied — download the image to post it"); }
  };
  const copy = async () => { await navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); };

  if (err) { return <div className="min-h-screen grid place-items-center p-6 text-center" data-testid="cert-error"><div><p className="font-display text-2xl font-bold text-navy">{err}</p><Link to="/support" className="underline text-turquoise">Back to Support Adventure Rock</Link></div></div>; }
  return (
    <div className="min-h-screen bg-sky-50 py-10 px-4" data-testid="cert-page">
      <div className="max-w-xl mx-auto space-y-5">
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-navy">{title}</h1>
        <p className="text-slate-600 text-sm sm:text-base">{intro}</p>
        {!d ? <p className="text-slate-400">Loading…</p> : (
          <>
            <canvas ref={ref} width={S} height={S} className="w-full rounded-3xl shadow-xl" data-testid="cert-canvas" aria-label={title} />
            <div className="flex flex-wrap gap-3">
              <Button onClick={download} className="rounded-full bg-[#FFB703] text-navy hover:bg-[#FFB703]/90 font-bold" data-testid="cert-download-btn"><Download className="w-4 h-4 mr-2" />Download image</Button>
              <Button onClick={share} variant="outline" className="rounded-full font-bold" data-testid="cert-share-btn"><Share2 className="w-4 h-4 mr-2" />Share</Button>
              <Button onClick={copy} variant="ghost" className="rounded-full" data-testid="cert-copy-btn"><Link2 className="w-4 h-4 mr-2" />Copy link</Button>
            </div>
            <p className="text-xs text-slate-500">{footer(d)}</p>
          </>
        )}
      </div>
    </div>
  );
}

export default function SponsorCertificate() {
  const { token } = useParams();
  return <ShareImagePage endpoint={`/support/certificate/${token}`} title="Your Partner Certificate" paint={draw}
    intro="Download it or share it straight to Instagram, Facebook or LinkedIn — and tag Adventure Rock Bermuda."
    fileName={(d) => `adventure-rock-partner-${d.business_name}`}
    shareText={(d) => `We're proud to be a ${d.tier_name} of Adventure Rock Bermuda! #SupportTheRock`}
    footer={(d) => `Commercial sponsorship of a privately operated business. Ref ${d.reference}.`} />;
}
